import { FormEvent, useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { useNavigate, useParams } from 'react-router'
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import LogoutIcon from '@mui/icons-material/Logout'
import DeleteIcon from '@mui/icons-material/Delete'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import FolderIcon from '@mui/icons-material/Folder'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EditIcon from '@mui/icons-material/Edit'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { isOrgAdminRole } from 'chat-shared'
import type { AssignmentScope, ChatSummary, Folder, Group, OrganizationSummary } from 'chat-shared'
import { api, ApiError, SharedUser } from '../api/client.js'
import { getChatSocket } from '../api/socket.js'
import { useAuth } from '../auth/AuthContext.js'
import { HappyOpsLogo } from './HappyOpsLogo.js'
import { readPreferredModel } from '../chat/modelPreference.js'
import { AssignmentPicker, scopeLabel } from './AssignmentPicker.js'

const shareErrorMessage = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.message === 'user_not_found') return 'Kein Nutzer mit dieser E-Mail-Adresse gefunden.'
    if (err.message === 'cannot_share_with_owner') return 'Dieser Nutzer besitzt den Chat bereits.'
    if (err.message === 'invalid_body') return 'Bitte eine gültige E-Mail-Adresse eingeben.'
  }
  return 'Teilen fehlgeschlagen'
}

export const Sidebar = () => {
  const [chats, setChats] = useState<Array<ChatSummary>>([])
  const [folders, setFolders] = useState<Array<Folder>>([])
  const [orgs, setOrgs] = useState<Array<OrganizationSummary>>([])
  const [groupsByOrg, setGroupsByOrg] = useState<Record<string, Array<Group>>>({})
  const navigate = useNavigate()
  const { chatId: activeChatId } = useParams<{ chatId: string }>()
  const { user, logout } = useAuth()

  const [deleteTarget, setDeleteTarget] = useState<ChatSummary | undefined>(undefined)

  const [shareTarget, setShareTarget] = useState<ChatSummary | undefined>(undefined)
  const [sharedUsers, setSharedUsers] = useState<Array<SharedUser>>([])
  const [shareEmail, setShareEmail] = useState('')
  const [shareError, setShareError] = useState<string | undefined>(undefined)

  const [assignTarget, setAssignTarget] = useState<ChatSummary | undefined>(undefined)
  const [chatRenameValue, setChatRenameValue] = useState('')

  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [newFolderOrgId, setNewFolderOrgId] = useState('')

  const [folderManageTarget, setFolderManageTarget] = useState<Folder | undefined>(undefined)
  const [folderRenameValue, setFolderRenameValue] = useState('')
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<Folder | undefined>(undefined)
  const [folderError, setFolderError] = useState<string | undefined>(undefined)

  const [orgPickerOpen, setOrgPickerOpen] = useState(false)
  const [pendingOrgId, setPendingOrgId] = useState('')

  // Ordner starten immer zugeklappt: gemerkt wird, was der User aufgeklappt
  // hat — nicht umgekehrt.
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [dragOverFolderId, setDragOverFolderId] = useState<string | undefined>(undefined)

  const reload = () => {
    api.listChats().then(setChats).catch(() => setChats([]))
  }
  const reloadFolders = () => {
    api.listFolders().then(setFolders).catch(() => setFolders([]))
  }

  useEffect(reload, [])
  useEffect(reloadFolders, [])
  useEffect(() => {
    api.listOrganizations().then(setOrgs).catch(() => setOrgs([]))
  }, [])
  // Titel-Änderungen (Umbenennen im Chat-Header, Auto-Titel nach der ersten
  // Nachricht) live übernehmen, statt auf einen Reload zu warten.
  useEffect(() => {
    const socket = getChatSocket()
    const onTitle: Parameters<typeof socket.on<'chat:title'>>[1] = (payload) => {
      setChats((prev) =>
        prev.map((c) => (c.id === payload.chatId ? { ...c, title: payload.title } : c)),
      )
    }
    socket.on('chat:title', onTitle)
    return () => {
      socket.off('chat:title', onTitle)
    }
  }, [])

  const loadGroups = (orgId: string) => {
    if (groupsByOrg[orgId]) return
    api
      .listGroups(orgId)
      .then((groups) => setGroupsByOrg((prev) => ({ ...prev, [orgId]: groups })))
      .catch(() => undefined)
  }

  // Jeder neue Chat muss einer Organisation zugeordnet werden: bei genau
  // einer Organisation automatisch, sonst über den Auswahl-Dialog unten.
  // Ohne Organisation (z.B. brandneuer User, den noch kein Admin zugewiesen
  // hat) wird der Chat ausnahmsweise ohne Zuweisung angelegt.
  const createChat = async () => {
    if (orgs.length >= 2) {
      setPendingOrgId('')
      setOrgPickerOpen(true)
      return
    }
    await doCreateChat(orgs.length === 1 ? orgs[0].id : undefined)
  }

  // Modell für neue Chats: gespeicherte Präferenz, sofern sie noch im
  // DB-Katalog existiert — sonst das erste aktivierte Modell.
  const resolveNewChatModel = async (): Promise<string> => {
    const models = await api.models()
    const preferred = readPreferredModel()
    if (preferred !== undefined && models.some((entry) => entry.id === preferred)) {
      return preferred
    }
    const first = models.at(0)?.id
    if (first === undefined) throw new Error('Keine Modelle verfügbar')
    return first
  }

  const doCreateChat = async (organizationId: string | undefined) => {
    const chat = await api.createChat(await resolveNewChatModel())
    let assignment: AssignmentScope | undefined
    if (organizationId) {
      assignment = { type: 'organization', organizationId, access: 'write' }
      await api.assignChat(chat.id, assignment)
    }
    setChats((prev) => [
      {
        id: chat.id,
        title: chat.title,
        model: chat.model,
        updatedAt: chat.updatedAt,
        ownerId: chat.userId,
        sharedWith: [],
        access: 'owner',
        assignment,
      },
      ...prev,
    ])
    navigate(`/chats/${chat.id}`)
  }

  // Neuer Chat direkt in einem Ordner: der Chat übernimmt die Zuweisung des
  // Ordners (Organisation bzw. Gruppe) und landet sofort darin. Hat der Ordner
  // (noch) keine Zuweisung, greift der Einzel-Organisation-Fallback wie bei
  // createChat.
  const createChatInFolder = async (folder: Folder): Promise<void> => {
    const chat = await api.createChat(await resolveNewChatModel())
    const fallback: AssignmentScope | undefined =
      orgs.length === 1
        ? { type: 'organization', organizationId: orgs[0].id, access: 'write' }
        : undefined
    const assignment = folder.assignment ?? fallback
    if (assignment) await api.assignChat(chat.id, assignment)
    await api.setChatFolder(chat.id, folder.id)
    setChats((prev) => [
      {
        id: chat.id,
        title: chat.title,
        model: chat.model,
        updatedAt: chat.updatedAt,
        ownerId: chat.userId,
        sharedWith: [],
        access: 'owner',
        assignment,
        folderId: folder.id,
      },
      ...prev,
    ])
    setExpandedFolders((prev) => {
      if (prev.has(folder.id)) return prev
      const next = new Set(prev)
      next.add(folder.id)
      return next
    })
    navigate(`/chats/${chat.id}`)
  }

  const handleConfirmOrgPicker = async () => {
    if (!pendingOrgId) return
    setOrgPickerOpen(false)
    await doCreateChat(pendingOrgId)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    await api.deleteChat(deleteTarget.id)
    setChats((prev) => prev.filter((chat) => chat.id !== deleteTarget.id))
    if (deleteTarget.id === activeChatId) navigate('/')
    setDeleteTarget(undefined)
  }

  const openShareDialog = (chat: ChatSummary) => {
    setShareTarget(chat)
    setShareEmail('')
    setShareError(undefined)
    api.listChatShares(chat.id).then(setSharedUsers).catch(() => setSharedUsers([]))
  }

  const closeShareDialog = () => {
    setShareTarget(undefined)
    setSharedUsers([])
  }

  const handleShareSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!shareTarget) return
    setShareError(undefined)
    try {
      const shared = await api.shareChat(shareTarget.id, shareEmail)
      setSharedUsers((prev) => [...prev, shared])
      setShareEmail('')
    } catch (err) {
      setShareError(shareErrorMessage(err))
    }
  }

  const handleUnshare = async (userId: string) => {
    if (!shareTarget) return
    await api.unshareChat(shareTarget.id, userId)
    setSharedUsers((prev) => prev.filter((sharedUser) => sharedUser.id !== userId))
  }

  const openChatManage = (chat: ChatSummary) => {
    setAssignTarget(chat)
    setChatRenameValue(chat.title)
  }

  const handleRenameChat = async () => {
    if (!assignTarget) return
    await api.renameChat(assignTarget.id, chatRenameValue)
    setChats((prev) => prev.map((c) => (c.id === assignTarget.id ? { ...c, title: chatRenameValue } : c)))
    setAssignTarget((prev) => (prev ? { ...prev, title: chatRenameValue } : prev))
  }

  const handleSetChatFolder = async (folderId: string) => {
    if (!assignTarget) return
    await api.setChatFolder(assignTarget.id, folderId || null)
    const nextFolderId = folderId || undefined
    setChats((prev) => prev.map((c) => (c.id === assignTarget.id ? { ...c, folderId: nextFolderId } : c)))
    setAssignTarget((prev) => (prev ? { ...prev, folderId: nextFolderId } : prev))
  }

  const handleAssignChat = async (scope: AssignmentScope) => {
    if (!assignTarget) return
    await api.assignChat(assignTarget.id, scope)
    setChats((prev) => prev.map((c) => (c.id === assignTarget.id ? { ...c, assignment: scope } : c)))
    setAssignTarget((prev) => (prev ? { ...prev, assignment: scope } : prev))
  }

  const handleUnassignChat = async () => {
    if (!assignTarget) return
    await api.unassignChat(assignTarget.id)
    setChats((prev) => prev.map((c) => (c.id === assignTarget.id ? { ...c, assignment: undefined } : c)))
    setAssignTarget((prev) => (prev ? { ...prev, assignment: undefined } : prev))
  }

  // Wie bei Chats muss auch jeder neue Ordner einer Organisation zugeordnet
  // werden. Ohne Organisation (z.B. brandneuer User, den noch kein Admin
  // zugewiesen hat) wird der Ordner ausnahmsweise ohne Zuweisung angelegt.
  const handleCreateFolder = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setFolderError(undefined)
    try {
      const folder = await api.createFolder(newFolderName)
      let assignment: AssignmentScope | undefined
      if (newFolderOrgId) {
        assignment = { type: 'organization', organizationId: newFolderOrgId, access: 'write' }
        await api.assignFolder(folder.id, assignment)
      }
      setFolders((prev) => [...prev, { ...folder, assignment }])
      setNewFolderName('')
      setCreatingFolder(false)
    } catch {
      setFolderError('Ordner anlegen fehlgeschlagen')
    }
  }

  const openFolderManage = (folder: Folder) => {
    setFolderManageTarget(folder)
    setFolderRenameValue(folder.name)
    setFolderError(undefined)
  }

  const handleRenameFolder = async () => {
    if (!folderManageTarget) return
    await api.renameFolder(folderManageTarget.id, folderRenameValue)
    setFolders((prev) =>
      prev.map((f) => (f.id === folderManageTarget.id ? { ...f, name: folderRenameValue } : f)),
    )
    setFolderManageTarget((prev) => (prev ? { ...prev, name: folderRenameValue } : prev))
  }

  const handleAssignFolder = async (scope: AssignmentScope) => {
    if (!folderManageTarget) return
    await api.assignFolder(folderManageTarget.id, scope)
    setFolders((prev) => prev.map((f) => (f.id === folderManageTarget.id ? { ...f, assignment: scope } : f)))
    setFolderManageTarget((prev) => (prev ? { ...prev, assignment: scope } : prev))
  }

  const handleUnassignFolder = async () => {
    if (!folderManageTarget) return
    await api.unassignFolder(folderManageTarget.id)
    setFolders((prev) => prev.map((f) => (f.id === folderManageTarget.id ? { ...f, assignment: undefined } : f)))
    setFolderManageTarget((prev) => (prev ? { ...prev, assignment: undefined } : prev))
  }

  const handleDeleteFolder = async () => {
    if (!deleteFolderTarget) return
    await api.deleteFolder(deleteFolderTarget.id)
    setFolders((prev) => prev.filter((f) => f.id !== deleteFolderTarget.id))
    setDeleteFolderTarget(undefined)
    reload()
  }

  const toggleFolder = (folderId: string): void => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const moveChatToFolder = async (chatId: string, folderId: string): Promise<void> => {
    const chat = chats.find((c) => c.id === chatId)
    if (!chat || chat.folderId === folderId) return
    await api.setChatFolder(chatId, folderId)
    setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, folderId } : c)))
    // Ziel-Ordner aufklappen, damit der verschobene Chat sichtbar ist
    setExpandedFolders((prev) => {
      if (prev.has(folderId)) return prev
      const next = new Set(prev)
      next.add(folderId)
      return next
    })
  }

  const ownFolderIds = new Set(folders.filter((f) => f.ownerId === user?.id).map((f) => f.id))
  const chatsByFolder = new Map<string, Array<ChatSummary>>()
  const unfiledChats: Array<ChatSummary> = []
  for (const chat of chats) {
    if (chat.folderId) {
      chatsByFolder.set(chat.folderId, [...(chatsByFolder.get(chat.folderId) ?? []), chat])
    } else {
      unfiledChats.push(chat)
    }
  }

  const renderChatItem = (chat: ChatSummary, indented = false) => {
    // Owner-Level statt Eigentümerschaft: Admins verwalten geteilte Chats wie
    // eigene (Umbenennen/Verschieben/Teilen/Löschen), siehe
    // resolveChatAccessLevel in chat-shared.
    const isOwner = chat.access === 'owner'
    const relative = DateTime.fromISO(chat.updatedAt).toRelative({ locale: 'de' })
    return (
      <ListItem
        key={chat.id}
        disablePadding
        // Die Action-Icons auf Höhe der Zeitstempel-Zeile setzen (statt
        // vertikal zentriert über beide Zeilen) — so bleibt die Titelzeile
        // in voller Breite lesbar.
        sx={{
          '& .MuiListItemSecondaryAction-root': {
            top: 'auto',
            bottom: 4,
            transform: 'none',
          },
        }}
        secondaryAction={
          isOwner ? (
            <Stack direction="row">
              <IconButton size="small" edge="end" onClick={() => openChatManage(chat)}>
                <DriveFileMoveOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" edge="end" onClick={() => openShareDialog(chat)}>
                <PersonAddIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" edge="end" onClick={() => setDeleteTarget(chat)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ) : undefined
        }
      >
        <ListItemButton
          selected={chat.id === activeChatId}
          onClick={() => navigate(`/chats/${chat.id}`)}
          draggable={isOwner}
          onDragStart={(e) => {
            e.dataTransfer.setData('application/x-chat-id', chat.id)
            e.dataTransfer.effectAllowed = 'move'
          }}
          sx={{
            // Die Icons liegen auf der Zeitstempel-Zeile, daher braucht nur
            // diese Platz rechts (siehe secondaryTypographyProps) — die
            // Titelzeile darf die volle Breite nutzen.
            ...(isOwner && { '&&': { pr: 2 } }),
            ...(indented && { pl: 4 }),
          }}
        >
          <ListItemText
            primary={chat.title}
            secondary={
              isOwner
                ? relative
                : `${relative} · ${chat.access === 'read' ? 'nur lesen' : 'geteilt'}`
            }
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true, sx: isOwner ? { pr: 10 } : undefined }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  return (
    <Box
      width={320}
      height="100%"
      display="flex"
      flexDirection="column"
      borderRight="1px solid"
      borderColor="divider"
    >
      <Box display="flex" alignItems="center" gap={1} px={2} pt={2}>
        <HappyOpsLogo width={28} height={28} />
        <Typography variant="h6" fontWeight={700}>
          HappyOps
        </Typography>
      </Box>
      <Box p={2}>
        <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={createChat}>
          Neuer Chat
        </Button>
      </Box>
      <List sx={{ flex: 1, overflow: 'auto' }}>
        {folders.map((folder) => {
          const folderChats = chatsByFolder.get(folder.id) ?? []
          if (folderChats.length === 0 && !ownFolderIds.has(folder.id)) return null
          const isOwnFolder = ownFolderIds.has(folder.id)
          const collapsed = !expandedFolders.has(folder.id)
          return (
            <Box key={folder.id}>
              <ListItem
                disablePadding
                secondaryAction={
                  isOwnFolder ? (
                    <Stack direction="row">
                      <IconButton
                        size="small"
                        edge="end"
                        title="Neuer Chat in diesem Ordner"
                        onClick={() => void createChatInFolder(folder)}
                      >
                        <AddCommentOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" edge="end" onClick={() => openFolderManage(folder)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" edge="end" onClick={() => setDeleteFolderTarget(folder)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ) : undefined
                }
              >
                <ListItemButton
                  onClick={() => toggleFolder(folder.id)}
                  onDragOver={(e) => {
                    if (!isOwnFolder || !e.dataTransfer.types.includes('application/x-chat-id')) return
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    setDragOverFolderId(folder.id)
                  }}
                  onDragLeave={() =>
                    setDragOverFolderId((prev) => (prev === folder.id ? undefined : prev))
                  }
                  onDrop={(e) => {
                    e.preventDefault()
                    setDragOverFolderId(undefined)
                    const chatId = e.dataTransfer.getData('application/x-chat-id')
                    if (isOwnFolder && chatId) void moveChatToFolder(chatId, folder.id)
                  }}
                  sx={{
                    py: 0.5,
                    gap: 1,
                    ...(isOwnFolder && { '&&': { pr: 12 } }),
                    ...(dragOverFolderId === folder.id && { bgcolor: 'action.selected' }),
                  }}
                >
                  {collapsed ? (
                    <ChevronRightIcon fontSize="small" color="action" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" color="action" />
                  )}
                  <FolderIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {folder.name}
                  </Typography>
                </ListItemButton>
              </ListItem>
              <Collapse in={!collapsed} timeout="auto">
                <List disablePadding>{folderChats.map((chat) => renderChatItem(chat, true))}</List>
              </Collapse>
            </Box>
          )
        })}
        {unfiledChats.map((chat) => renderChatItem(chat))}
        {chats.length === 0 && (
          <Typography variant="body2" color="text.secondary" p={2}>
            Noch keine Chats.
          </Typography>
        )}
      </List>
      <Divider />
      <Stack p={1}>
        <Button
          startIcon={<CreateNewFolderIcon />}
          onClick={() => {
            setCreatingFolder(true)
            setNewFolderName('')
            setNewFolderOrgId(orgs.length === 1 ? orgs[0].id : '')
            setFolderError(undefined)
          }}
          sx={{ justifyContent: 'flex-start' }}
        >
          Neuer Ordner
        </Button>
        {(user?.role === 'system-admin' || orgs.some((org) => isOrgAdminRole(org.role))) && (
          <Button
            startIcon={<AdminPanelSettingsIcon />}
            onClick={() => navigate('/settings')}
            sx={{ justifyContent: 'flex-start' }}
          >
            Einstellungen
          </Button>
        )}
        <Button
          startIcon={<LogoutIcon />}
          onClick={() => void logout()}
          sx={{ justifyContent: 'flex-start' }}
        >
          Logout
        </Button>
      </Stack>

      <Dialog open={orgPickerOpen} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle>Organisation wählen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Für den neuen Chat muss eine Organisation ausgewählt werden.
          </Typography>
          <Select
            fullWidth
            size="small"
            value={pendingOrgId}
            onChange={(e) => setPendingOrgId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Organisation wählen
            </MenuItem>
            {orgs.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" disabled={!pendingOrgId} onClick={() => void handleConfirmOrgPicker()}>
            Chat erstellen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(undefined)}>
        <DialogTitle>Chat löschen?</DialogTitle>
        <DialogContent>
          „{deleteTarget?.title}“ wird unwiderruflich gelöscht, inklusive aller Nachrichten.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(undefined)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleDelete()}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(shareTarget)} onClose={closeShareDialog} maxWidth="xs" fullWidth>
        <DialogTitle>„{shareTarget?.title}“ teilen</DialogTitle>
        <DialogContent>
          {shareError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShareError(undefined)}>
              {shareError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleShareSubmit} display="flex" gap={1} mb={2}>
            <TextField
              label="E-Mail-Adresse"
              type="email"
              size="small"
              fullWidth
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              required
            />
            <Button type="submit" variant="contained">
              Teilen
            </Button>
          </Box>
          <List dense disablePadding>
            {sharedUsers.map((sharedUser) => (
              <ListItem
                key={sharedUser.id}
                disablePadding
                secondaryAction={
                  <IconButton size="small" onClick={() => void handleUnshare(sharedUser.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText primary={sharedUser.displayName} secondary={sharedUser.email} />
              </ListItem>
            ))}
            {sharedUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Noch mit niemandem geteilt.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShareDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        key={assignTarget?.id}
        open={Boolean(assignTarget)}
        onClose={() => setAssignTarget(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Chat verwalten</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} mb={3}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={chatRenameValue}
              onChange={(e) => setChatRenameValue(e.target.value)}
            />
            <Button variant="contained" onClick={() => void handleRenameChat()}>
              Speichern
            </Button>
          </Box>

          <Typography variant="subtitle2" mb={1}>
            Ordner
          </Typography>
          <Select
            size="small"
            fullWidth
            value={assignTarget?.folderId ?? ''}
            onChange={(e) => void handleSetChatFolder(e.target.value)}
            sx={{ mb: 3 }}
          >
            <MenuItem value="">Kein Ordner</MenuItem>
            {folders
              .filter((f) => f.ownerId === user?.id)
              .map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.name}
                </MenuItem>
              ))}
          </Select>

          <Typography variant="subtitle2" mb={1}>
            Organisation / Gruppe
          </Typography>
          {assignTarget?.assignment && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button size="small" onClick={() => void handleUnassignChat()}>
                  Entfernen
                </Button>
              }
            >
              {scopeLabel(assignTarget.assignment, orgs, groupsByOrg)}
            </Alert>
          )}
          <AssignmentPicker orgs={orgs} groupsByOrg={groupsByOrg} onLoadGroups={loadGroups} onSubmit={(scope) => void handleAssignChat(scope)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTarget(undefined)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={creatingFolder} onClose={() => setCreatingFolder(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Neuer Ordner</DialogTitle>
        <DialogContent>
          {folderError && <Alert severity="error">{folderError}</Alert>}
          <Box
            component="form"
            onSubmit={handleCreateFolder}
            display="flex"
            flexDirection="column"
            gap={1}
            mt={1}
          >
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              required
              autoFocus
            />
            {orgs.length > 0 && (
              <Select
                size="small"
                fullWidth
                value={newFolderOrgId}
                onChange={(e) => setNewFolderOrgId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Organisation wählen
                </MenuItem>
                {orgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={orgs.length > 0 && !newFolderOrgId}
            >
              Anlegen
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        key={folderManageTarget?.id}
        open={Boolean(folderManageTarget)}
        onClose={() => setFolderManageTarget(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ordner verwalten</DialogTitle>
        <DialogContent>
          {folderError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {folderError}
            </Alert>
          )}
          <Box display="flex" gap={1} mb={3}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={folderRenameValue}
              onChange={(e) => setFolderRenameValue(e.target.value)}
            />
            <Button variant="contained" onClick={() => void handleRenameFolder()}>
              Speichern
            </Button>
          </Box>

          <Typography variant="subtitle2" mb={1}>
            Organisation / Gruppe (gilt für alle Chats im Ordner)
          </Typography>
          {folderManageTarget?.assignment && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button size="small" onClick={() => void handleUnassignFolder()}>
                  Entfernen
                </Button>
              }
            >
              {scopeLabel(folderManageTarget.assignment, orgs, groupsByOrg)}
            </Alert>
          )}
          <AssignmentPicker
            orgs={orgs}
            groupsByOrg={groupsByOrg}
            onLoadGroups={loadGroups}
            onSubmit={(scope) => void handleAssignFolder(scope)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderManageTarget(undefined)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteFolderTarget)} onClose={() => setDeleteFolderTarget(undefined)}>
        <DialogTitle>Ordner löschen?</DialogTitle>
        <DialogContent>
          „{deleteFolderTarget?.name}“ wird gelöscht. Enthaltene Chats bleiben erhalten und werden in
          „Kein Ordner“ verschoben.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderTarget(undefined)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleDeleteFolder()}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
